import { loadVimeoApi } from '../embed-sdk.loader';
import { EmbedAdapterOptions, EmbedVideoAdapter } from '../embed-video.model';

declare const window: any;

const TRACKED_EVENTS = ['loaded', 'play', 'pause', 'ended', 'timeupdate', 'error'] as const;

/**
 * Same "we build the iframe, the SDK attaches to it" shape as the YouTube adapter — the branch
 * production reserved for Safari (`useDirectVimeoIframe`) collapses here into the only path, so it
 * behaves the same in every browser instead of three divergent ones.
 *
 * Vimeo's own `getCurrentTime()`/`getDuration()` are promise-based; the tracker samples synchronously
 * every tick, so this caches the last `timeupdate` instead of racing a promise on every sample.
 */
export class VimeoEmbedAdapter implements EmbedVideoAdapter {
  private player: any = null;
  private iframe: HTMLIFrameElement | null = null;
  private destroyed = false;
  private lastKnownTime = 0;
  private lastKnownDuration = 0;

  mount(host: HTMLElement, options: EmbedAdapterOptions): void {
    const iframe = document.createElement('iframe');
    iframe.id = options.playerId;
    iframe.setAttribute('frameborder', '0');
    iframe.setAttribute('allow', 'autoplay; fullscreen; picture-in-picture; encrypted-media');
    iframe.setAttribute('allowfullscreen', 'true');
    iframe.style.width = '100%';
    iframe.style.height = '100%';
    iframe.src =
      `https://player.vimeo.com/video/${options.videoId}` +
      `?autopause=0&dnt=1&playsinline=1&player_id=${options.playerId}`;
    host.appendChild(iframe);
    this.iframe = iframe;

    loadVimeoApi().then(() => {
      if (this.destroyed) return;
      this.player = new window.Vimeo.Player(iframe);

      this.player.on('loaded', () => {
        if (options.startAt > 0) this.player.setCurrentTime(options.startAt).catch(() => undefined);
        options.onEvent({ type: 'ready' });
      });
      this.player.on('play', () => options.onEvent({ type: 'playing' }));
      this.player.on('pause', () => options.onEvent({ type: 'paused', currentTime: this.lastKnownTime }));
      this.player.on('ended', () => options.onEvent({ type: 'ended', currentTime: this.lastKnownDuration }));
      this.player.on('timeupdate', (data: { seconds: number; duration: number }) => {
        this.lastKnownTime = data.seconds;
        this.lastKnownDuration = data.duration;
      });
      this.player.on('error', (error: unknown) => options.onEvent({ type: 'error', error }));
    });
  }

  getCurrentTime(): number {
    return this.lastKnownTime;
  }

  getDuration(): number {
    return this.lastKnownDuration;
  }

  seekTo(seconds: number): void {
    this.player?.setCurrentTime?.(seconds)?.catch?.(() => undefined);
  }

  play(): void {
    this.player?.play?.()?.catch?.(() => undefined);
  }

  pause(): void {
    this.player?.pause?.()?.catch?.(() => undefined);
  }

  destroy(): void {
    this.destroyed = true;
    try {
      // `off(event)` with no handler removes every listener for it — covers `loaded`/`error` too,
      // which production's own cleanup (`tema.page.ts`) forgets.
      TRACKED_EVENTS.forEach((event) => this.player?.off(event));
      this.player?.destroy?.();
    } catch {
      /* already gone */
    }
    this.player = null;
    this.iframe?.remove();
    this.iframe = null;
  }
}
